/**
 * Type utilities
 *	@module ods/types
 */

//
//	Required
//

import {z} from 'zod';

//
//	Types
//

type deepCompareType = 'equal' | 'valid' | 'atLeast' | 'atMost';

//
//	Constants
//

const schemaEmail = z.email();
const schemaUrl = z.url({protocol: /^https?$/});
// eslint-disable-next-line unicorn/better-regex
const phoneSchema = z.string().regex(/^\+?[0-9\s\-()]{1,60}$/, 'Invalid phone number');

//
//	API
//

/**
 * Test whether `value` is error object.
 *
 * @param value - The value to be checked
 * @returns return true if the value is an Error
 */
export const isError = (value: unknown): value is Error => {
	switch (Object.prototype.toString.call(value)) {
		case '[object Error]':
			return true;
		case '[object Exception]':
			return true;
		case '[object DOMException]':
			return true;
		default:
			return value instanceof Error;
	}
};

/**
 * Is the given value "undefined"
 *
 * @param value - The value to be checked
 * @returns returns true if the value has type "undefined"
 */
export const isUndefined = (value: unknown): value is undefined => value === undefined;

/**
 * Is the given value "null"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isNull = (value: unknown): value is null => value === null;

/**
 * Is the given value a "Object" but not null
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isObject = (value: unknown): boolean => {
	const type = typeof value;
	return value !== null && (type === 'object' || type === 'function');
};

/**
 * Is the given value a "plain old javascriot object"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isPlainOldJavaScriptObject = (value: unknown): boolean => {
	if (value === null || typeof value !== 'object') {
		return false;
	}

	return Object.getPrototypeOf(value) === Object.prototype;
};

/**
 * Is the given value a "flat plain old javascriot object"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isFlatPlainOldJavaScriptObject = (value: unknown): boolean =>
	isPlainOldJavaScriptObject(value) ? Object.values(value as Record<string, unknown>).every(isScalar) : false;

/**
 * Is the given value a "function"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const isFunction = (value: unknown): value is Function => {
	return !!(value && (value as {constructor?: unknown}).constructor && (value as {call?: unknown}).call && (value as {apply?: unknown}).apply);
};

/**
 * Is the given value a "string"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isString = (value: unknown): value is string => typeof value === 'string';

/**
 * Is the given value a "array of strings"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((e) => typeof e === 'string');

/**
 * Is the given value a "number"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isNumber = (value: unknown): value is number => typeof value === 'number' && !Number.isNaN(value) && Number.isFinite(value);

/**
 *	Is the given value an integer
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isInteger = (value: unknown): value is number => isNumber(value) && value % 1 === 0;

/**
 * Is the given value a "boolean"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isBoolean = (value: unknown): value is boolean => value === true || value === false || toString.call(value) === '[object Boolean]';

/**
 * Is the given value a "Date"
 *
 * @param value - The value to be checked
 * @returns returns true if the value matches the type
 */
export const isDate = (value: unknown): value is Date => toString.call(value) === '[object Date]';

/**
 * Check if string only contains digits or is empty.
 *
 * @param value - The value to be checked
 * @returns The state of the check
 */
export const isDigitsOnly = (value: unknown): boolean => typeof value === 'string' && /^\d*$/.test(value);

/**
 * Is the given value a valid email address.
 *
 * @param value - The value to be checked
 * @returns Return true if the value is a valid.
 */
export const isEmail = (value: unknown): boolean => schemaEmail.safeParse(value).success;

/**
 * Is the given value a valid url.
 *
 * @param value - The value to be checked
 * @returns Return true if the value is a valid.
 */
export const isUrl = (value: unknown): boolean => schemaUrl.safeParse(value).success;

/**
 * Is the given value a valid phone.
 *
 * @param value - The value to be checked
 * @returns Return true if the value is a valid.
 */
export const isPhone = (value: unknown): boolean => phoneSchema.safeParse(value).success;

/**
 * Checks if an object (obj) has the same members as another representative object (compareObj)
 * Checks if the members of obj have the same types as the members of compareObj
 * It also works if the objects are nested or they include arrays (of objects, arrays, ...)
 * It writes a specific error what is missing or of wrong type
 * A typical application is to check if an object has all required parameters if the parameters are complex objects
 *
 * @param obj - The type that the object should have
 * @param objName - The name of the object to be checked
 * @param compareObj - The (nested) object to compare with. It can 'nest' arrays, objects, and strings. Strings do always encode types.  (see example).
 * @param testFor - One of the following options:
 * 	if testFor = 'equal' then "obj" has to have EXACTLY the same members as "objName".
 *	if testFor = 'valid' then "obj" must typewise match the members as "objName"
 *	if testFor = 'atLeast' then "obj" has to have AT LEAST the same members as "objName"
 *	if testFor = 'atMost' then "obj" has to have AT MOST the same members as "objName"
 *
 * @example
 * obj = {nam: 'xyz', num: 'abc'}; // we want to check if this object has the form that we want
 *
 * checkParameterDeep(obj, 'obj', {nam: 'string', num: 'number'}, 'equal');
 * // output: Error: Expected type "number" for "obj.num". But the type of "obj.num" is "string"!
 *
 * @example
 * obj = {array1: [{id: 0, name: 'John'}, {id: 1}]}; // we want to check if this object has the form that we want
 *
 * checkParameterDeep(obj, 'obj', {array1: [{id: 'number', name: 'string'}]}, 'equal');
 * // output: Error: obj.array1[1] does not have the demanded member "name"!
 *
 * @example
 * obj = {array1: [{id: 0, name: 'John'}, {id: 1}]}; // we want to check if this object has the form that we want
 *
 * checkParameterDeep(obj, 'obj', {array1: [{id: 'number'}]}, 'valid');
 * // output: Ok because there is a member missing but the types are just fine!
 *
 * @example
 * obj = {array1: []}; // we want to check if this object has the form that we want
 *
 * checkParameterDeep(obj, 'obj', {array1: [{id: 'number', name: ''}]}, 'equal');
 * output: will write no error because array1 is empty
 *
 */
export const checkParameterDeep = (obj: unknown, objName: string, compareObj: unknown, testFor: deepCompareType): void => {
	if (!['equal', 'valid', 'atLeast', 'atMost'].includes(testFor)) {
		throw new Error(`Parameter testFor "${testFor}" is invalid`);
	}

	_checkParameterDeep(obj, objName, compareObj, testFor);
};

/*
 * (recursive) help function for the function checkParameterDeep
 */
function _checkParameterDeep(obj: unknown, objName: string, compareObj: unknown, testFor: deepCompareType): void {
	if (typeof compareObj === 'string') {
		// if the compare type is not 'arbitrary'
		if (compareObj !== 'arbitrary') {
			// compare with the type that is set by the string in the compare object
			checkVariableType(compareObj, obj, objName);
		}
	} else if (Array.isArray(compareObj)) {
		if (!Array.isArray(obj)) {
			throw new Error(checkVariableTypeError('array', obj, objName));
		}

		if (compareObj.length > 0) {
			for (let i = 0; i < obj.length; i++) {
				// compare each element in the array with the first element of the compare array
				_checkParameterDeep(obj[i], `${objName}[${i}]`, compareObj[0], testFor);
			}
		}
	} else if (typeof compareObj === 'object' && compareObj !== null) {
		if (typeof obj !== 'object' || obj === null) {
			throw new Error(checkVariableTypeError('object', obj, objName));
		}

		if (testFor === 'equal' || testFor === 'atMost') {
			for (const member in obj) {
				if (Object.hasOwn(obj, member)) {
					if (!Object.hasOwn(compareObj, member)) {
						throw new Error(`${objName} has the member "${member}" which does not exist in the parameter compare object`);
					} else {
						_checkParameterDeep(
							(obj as Record<string, unknown>)[member],
							`${objName}.${member}`,
							(compareObj as Record<string, unknown>)[member],
							testFor,
						);
					}
				}
			}
		}

		if (['equal', 'valid', 'atLeast'].includes(testFor)) {
			for (const member in compareObj) {
				if (Object.hasOwn(compareObj, member)) {
					if (!Object.hasOwn(obj, member)) {
						if (testFor !== 'valid') {
							throw new Error(`${objName} does not have the member "${member}" which is demanded by the parameter compare object`);
						}
					} else {
						_checkParameterDeep(
							(obj as Record<string, unknown>)[member],
							`${objName}.${member}`,
							(compareObj as Record<string, unknown>)[member],
							testFor,
						);
					}
				}
			}
		}
	} else {
		throw new Error(`The compare object contains the type "${typeof compareObj}" which is not allowed`);
	}
}

/*
 *	Return true, if the value is scalar (string, number, boolean, Date or null)
 */
function isScalar(value: unknown): boolean {
	const type = typeof value;

	return type === 'string' || type === 'number' || type === 'boolean' || type === 'undefined' || isDate(value) || value === null;
}

/*
 * to avoid complex notations with type array
 */
function typeOf(value: unknown): string {
	if (Array.isArray(value)) {
		return 'array';
	}

	return typeof value;
}

/*
 * Writes a specific error if a value does not have a given type
 */
function checkVariableType(type: string, value: unknown, name?: string): void {
	if (typeOf(value) !== type) {
		throw new Error(checkVariableTypeError(type, value, name));
	}
}

/*
 * Get error message for check* methods
 */
function checkVariableTypeError(type: string, value: unknown, name?: string): string {
	let error = `Actual type "${typeOf(value)}" does not match expected type "${type}"`;
	if (typeof name === 'string' && name.length > 0) {
		error += ` for value "${name}"`;
	}

	return error;
}
